import { createSignal, onMount, Show, useContext } from 'solid-js';
import { StyledErrorMessage, StyledFieldContainer, StyledShowpadCodeContainer } from './showpad-code-submission-style';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addShowpadCode } from '../../tools/add-showpad-code';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { StyledTextInput } from '../../ui-components/inputs/text-input';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Modal } from '../../ui-components/modal/modal';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Text } from '../../ui-components/text/text';
import { TabHandler } from '../menu/menu-desktop/tab-handling';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';

export const ShowpadCodeSubmission = () => {
    const [errorMessage, setErrorMessage] = createSignal('');
    const [showModal, setShowModal] = createSignal(false);
    const [code, setCode] = createSignal('');
    const [submitting, setSubmitting] = createSignal(false);
    const [success, setSuccess] = createSignal(false);

    const { 
        localize,
        userState,
        updateUser,
        contactUsPage,
    } = useContext(AppContext);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    onMount(() => {
        window.addEventListener('keydown', keydownListener);
    });

    const closeModal = () => {
        setShowModal(false);
    };

    const keydownListener = (e: any) => {
        if (showModal() && e.key === 'Escape') {
            closeModal();
        }
    };

    const handleInputChange = (value: string) => {
        setSuccess(false);
        setErrorMessage('');
        setCode(value);
    };

    const submitCode = async () => {
        setSubmitting(true);
        setSuccess(false);
        setErrorMessage('');

        const { success, message } = await addShowpadCode({ showpadCode: code() }, updateUser);
        setSubmitting(false);

        if (!success) {
            if (message === 'invalid-showpad-code') {
                setErrorMessage(localize('invalid-showpad-code', 'The code you entered is invalid'));
                setShowModal(true);
            } else {
                console.error('Error:', message);
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        setCode('');
        setSuccess(true);
    };

    const HelpModal = () => (

        <TabHandler refs={{
            firstTabbableElement,
            lastTabbableElement,
        }}>
            <Modal
                visible={true}
                onClose={() => closeModal()}
                size='small'
                darkenBackground={true}
                closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
            >
                <StyledVerticalSpace size={5} />
                <Text fontSize='large' fontStyle='bold'>
                    {localize('no-code-modal-header', 'Get access to courses')}
                </Text>

                <Text fontSize='normal' splitParagraph={true}>
                    {localize('no-code-modal-text', 'Please get in touch with us to get access to our e-learning catalog.')}
                </Text>

                <StyledFlexRow gap='2rem' justifyContent='center' alignItems='center'>
                    <Button
                        label={localize('close-modal-button-text', 'Close')}
                        variant='secondary'
                        onClick={closeModal}
                    />

                    <Button
                        label={localize('contact-us', 'Contact us')}
                        isLink={true}
                        url={contactUsPage.url}
                        element={(el: HTMLElement) => {
                            setLastTabbableElement(el);
                        }}
                    />
                </StyledFlexRow>

            </Modal>
        </TabHandler>
    );

    return (
        <Show when={userState.user}>
            <StyledShowpadCodeContainer>

                <Heading
                    variant='smallGray'
                    tag='h2'
                    noBlockSpacing
                >
                    {localize('course-access-code', 'Course access code')}
                </Heading>

                <StyledFieldContainer>
                    <StyledFlexRow gap='1.5rem' flexWrap='wrap'>
                        <StyledTextInput
                            value={code()}
                            onInput={(e) => handleInputChange(e.currentTarget.value)}
                            placeholder={userState.user?.showpadCode}
                            hasErrors={false}
                            name={'showpad-code'}
                            monospace
                            width='35rem'
                        />

                        <Button
                            label={localize('submit-code', 'Submit code')}
                            onClick={() => submitCode()}
                            variant='secondary'
                            noBlockSpacing
                            disabled={submitting()}
                        />

                        <Show when={submitting()}>
                            <LoadingSpinner size={'2.2rem'} />
                        </Show>
                    </StyledFlexRow>


                    <Show when={errorMessage()}>
                        <StyledErrorMessage>
                            {errorMessage()}
                        </StyledErrorMessage>
                    </Show>

                    <Show when={success()}>
                        <StyledVerticalSpace size={0.5} />
                        <ShowpadDelayInfoBox
                            withoutPadding={true}
                            text={localize('showpad-code-successfully-added', 'Your Showpad access code has been successfully added. Please note that it may take up to an hour for your access to be updated. Check back later to view your courses.')}
                        />
                    </Show>
                </StyledFieldContainer>

            </StyledShowpadCodeContainer>

            <Show when={showModal()}>
                <HelpModal />
            </Show>
            
        </Show>
    );
};